import { useEffect, useRef, useState } from 'react';
import millify from 'millify';

import {
  AxisLabel,
  Container,
  DetailsSection,
  DragCircle,
  HighlightedLine,
  IconCross,
  InfoLabel,
  InputDelimiterLabel,
  InputField,
  SliderContainer,
  SliderLine,
  SliderSection,
} from './styles';

const DragType = {
  DragMin: 'DRAG_MIN',
  DragMax: 'DRAG_MAX',
};

const getInitialDraggingState = () => ({ dragType: null });

const calculatePercentageValueByRealValueAndRange = (value, range) => {
  const { min, max } = range;

  const domain = max - min;
  const normalizedFactor = domain / 100;
  return (value - min) / normalizedFactor;
};

const calculateRealValueByPercentageValueAndRange = (percentage, range) => {
  const { min, max } = range;

  const domain = max - min;
  const normalizedFactor = domain / 100;

  return percentage * normalizedFactor + min;
};

const Slider = ({ defaultValue, range, unit, infoLabel, onChange, onClose }) => {
  const sliderLineRef = useRef();

  const [minPercentageValue, setMinPercentageValue] = useState(
    calculatePercentageValueByRealValueAndRange(defaultValue.min, range)
  );

  const [maxPercentageValue, setMaxPercentageValue] = useState(
    calculatePercentageValueByRealValueAndRange(defaultValue.max, range)
  );

  const [isDragging, setIsDragging] = useState(true);

  const [inputMinValue, setInputMinValue] = useState(defaultValue.min);
  const [inputMaxValue, setInputMaxValue] = useState(defaultValue.max);

  let draggingState = getInitialDraggingState();

  const highlightLineStyles = { width: maxPercentageValue - minPercentageValue, left: minPercentageValue };

  const handleMouseUp = () => {
    onDragMouseUp();
  };

  const handleMouseMove = (event) => {
    onDragMouseMove(event);
  };

  const onDragMouseDown = (event, dragType) => {
    setIsDragging(true);
    draggingState = {
      dragType,
    };

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
  };

  const onDragMouseUp = () => {
    window.removeEventListener('mouseup', handleMouseUp);
    window.removeEventListener('mousemove', handleMouseMove);
    draggingState = getInitialDraggingState();
    setIsDragging(false);
  };

  const onDragMouseMove = (event) => {
    const { x, width } = sliderLineRef.current.getBoundingClientRect();
    const { screenX } = event;
    const percentage = ((screenX - x) * 100) / width;

    const getLowerBound = (value, upperBound) => Math.min(Math.max(0, value), upperBound - 1);
    const getUpperBound = (value, lowerBound) => Math.max(Math.min(value, 100), lowerBound + 1);

    const updateValue =
      draggingState.dragType === DragType.DragMin
        ? () => setMinPercentageValue(getLowerBound(percentage, maxPercentageValue))
        : () => setMaxPercentageValue(getUpperBound(percentage, minPercentageValue));

    updateValue();
  };

  const realMinValue = calculateRealValueByPercentageValueAndRange(minPercentageValue, range).toFixed(2);
  const realMaxValue = calculateRealValueByPercentageValueAndRange(maxPercentageValue, range).toFixed(2);

  const onInputValueChange = (event, dragType) => {
    const value = Number(event.target.value);
    const valueInPercentage = calculatePercentageValueByRealValueAndRange(value, range);

    if (dragType === DragType.DragMin) {
      setInputMinValue(value);
    } else {
      setInputMaxValue(value);
    }

    if (valueInPercentage < 0 || valueInPercentage > 100) {
      return;
    }

    if (dragType === DragType.DragMin) {
      const isValueBiggerThanMaxValue = valueInPercentage > maxPercentageValue - 1;

      if (isValueBiggerThanMaxValue) {
        return;
      }

      setMinPercentageValue(valueInPercentage);
    }

    if (dragType === DragType.DragMax) {
      const isValueSmallerThanMinValue = valueInPercentage < minPercentageValue + 1;

      if (isValueSmallerThanMinValue) {
        return;
      }

      setMaxPercentageValue(valueInPercentage);
    }
  };

  useEffect(() => {
    setInputMinValue(realMinValue);
    setInputMaxValue(realMaxValue);
  }, [realMinValue, realMaxValue]);

  useEffect(() => {
    const shouldEmitChange = !isDragging;

    if (shouldEmitChange) {
      onChange({ min: Number(realMinValue), max: Number(realMaxValue) });
    }
  }, [isDragging, realMinValue, realMaxValue]);

  return (
    <Container>
      <DetailsSection>
        <InfoLabel>{infoLabel}</InfoLabel>
        <InputField
          value={inputMinValue}
          type='number'
          onChange={(e) => onInputValueChange(e, DragType.DragMin)}
        />
        <InputDelimiterLabel>to</InputDelimiterLabel>
        <InputField
          value={inputMaxValue}
          type='number'
          onChange={(e) => onInputValueChange(e, DragType.DragMax)}
        />
        <InputDelimiterLabel>{unit}</InputDelimiterLabel>
      </DetailsSection>
      <SliderSection>
        <AxisLabel>{millify(range.min)}</AxisLabel>
        <SliderContainer>
          <SliderLine ref={sliderLineRef}>
            <HighlightedLine highlightLineStyles={highlightLineStyles} />
            <DragCircle
              percentageValue={minPercentageValue}
              onMouseDown={(event) => onDragMouseDown(event, DragType.DragMin)}
            />
            <DragCircle
              percentageValue={maxPercentageValue}
              onMouseDown={(event) => onDragMouseDown(event, DragType.DragMax)}
            />
          </SliderLine>
        </SliderContainer>
        <AxisLabel>{millify(range.max)}</AxisLabel>
      </SliderSection>
      <IconCross onClick={() => onClose()}>X</IconCross>
    </Container>
  );
};

export default Slider;
